import { CloseOutlined, SyncOutlined } from '@ant-design/icons';
import { Button, ButtonProps, RadioInput, Status } from '@mm/components';
import { useConnectionCRUDActions } from '@mm/state/actions/ConnectionsCRUDActions';
import { useSalesForceCRUDActions } from '@mm/state/actions/SalesForceCRUDActions';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { SalesForceAccount } from '@mm/state/model/SalesForceModel';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { selectCurrentUserId } from '@mm/state/selectors/userSelector';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { StoreState } from '@mm/state/store-interfaces';
import { AudienceSimpleSelect } from '@mm/stateful-components';
import { colors } from '@mm/utils';
import { Switch } from 'antd';
import classNames from 'classnames';
import React, { useState, MouseEvent, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import styles from './SalesForceIntegrations.module.css';
import { useConnectSalesForce } from './use-connect-salesforce';

export const SalesForceSettings = ({
  connection,
  account,
  oAuthButton,
}: {
  connection: ConnectionModel;
  account: SalesForceAccount;
  oAuthButton: (
    buttonName?: string | ReactNode,
    buttonProps?: Pick<ButtonProps, 'size' | 'color' | 'css' | 'disabled'>,
  ) => ReactNode;
}) => {
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const currentUserId = useSelector(selectCurrentUserId);
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;
  const [radioValue, setRadioValue] = useState<'all' | 'audience'>(
    connection?.audienceId ? 'audience' : 'all',
  );
  const accounts = useSelector(
    (state: StoreState) => state.salesForce.accounts,
  );
  const salesForceAccount = accounts?.[0];
  const erroredAccount = salesForceAccount?.status === 'error';

  const { update } = useConnectionCRUDActions();
  const { revokeAccess } = useSalesForceCRUDActions();
  const { retrieveSalesForceAccounts } = useConnectSalesForce();

  const updateWebhook = async (params: Partial<ConnectionModel>) => {
    const isDeactivatingConnection = !connection?.active;

    if (
      !connection ||
      (!isDeactivatingConnection && !hasNativeIntegrationsFeature)
    ) {
      return;
    }

    await update(facebookPageId, {
      id: connection.id,
      ...params,
    });
  };

  const revoke = async (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    if (account) {
      const revoked = await revokeAccess(account.id, connection.id);
      if (revoked) {
        retrieveSalesForceAccounts();
      }
    }
  };

  return (
    <div className={styles.settingsContent}>
      <div className={classNames(styles.box, styles.boxWithHelp)}>
        <div className={styles.boxContent}>
          <div className={styles.heading}>Connect to SalesForce directly</div>
          <div className={styles.connectionBlock}>
            <Status
              status={erroredAccount ? 'Error' : 'Connected'}
              color={erroredAccount ? colors.red : colors.green5}
              className={styles.status}
              css={{ statusIndicator: styles.statusIndicator }}
            />

            {oAuthButton(
              <>
                <SyncOutlined />
                Reconnect
              </>,
              {
                color: 'secondary-blue',
                css: { container: styles.button },
                disabled: account?.readOnly,
              },
            )}
            {account && (
              <Button
                css={{ container: styles.button }}
                color="secondary-blue"
                onClick={revoke}
                disabled={account?.readOnly}
              >
                <CloseOutlined />
                Disconnect
              </Button>
            )}
          </div>
          <div className={styles.error}>{salesForceAccount?.errorInfo}</div>
        </div>
      </div>

      <div
        className={classNames(styles.box, {
          [styles.boxDisabled]:
            connection.salesforceAccessToken?.userId !== currentUserId ||
            !account,
        })}
      >
        <div className={styles.boxWithHelp}>
          <div className={classNames(styles.heading, styles.headingWithSwitch)}>
            Contact sync
            <Switch
              defaultChecked={connection?.active}
              checkedChildren="Yes"
              unCheckedChildren="No"
              className={styles.switch}
              onChange={ok => updateWebhook({ active: ok })}
              disabled={!connection}
            />
          </div>
        </div>
        <div className={styles.radioInputs}>
          <RadioInput
            name="all"
            value="all"
            label="Sync all Customers.ai contacts to SalesForce"
            onChange={() => {
              setRadioValue('all');
              updateWebhook({ audienceId: null });
            }}
            checked={radioValue === 'all'}
            disabled={!connection}
          />
          <RadioInput
            name="audience"
            value="audience"
            label="Choose an audience to sync from "
            onChange={() => setRadioValue('audience')}
            checked={radioValue === 'audience'}
            disabled={!connection}
          />
          {radioValue === 'audience' && (
            <AudienceSimpleSelect
              className={styles.audienceSelect}
              selectedAudienceId={connection?.audienceId || undefined}
              onChange={audienceId => updateWebhook({ audienceId })}
            />
          )}
          <div className={styles.note}>
            All leads will be tagged “Customers.ai”
          </div>
        </div>
      </div>
    </div>
  );
};
